"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { ChevronRight, X } from "lucide-react";
import type { Locale } from "@/lib/i18n";
import type { PublicPlaygroundPromo } from "@/lib/platform-public-settings";
import { getLocalizedField } from "@/features/courses/api";
import { formatBdt } from "@/features/courses/lib/card-price";
import { formatPrice } from "@/lib/currency";
import { formatDate } from "@/lib/format-datetime";
import { cfImageUrl } from "@/lib/cloudflare-image";
import { useOptionalAuth } from "@/components/providers/auth-provider";
import { useLibrary } from "@/features/dashboard/hooks";
import {
  PLAYGROUND_PROMO_DISMISSAL_KEY,
  clearPlaygroundPromoDismissed,
  persistPlaygroundPromoDismissed,
} from "@/lib/playground-promo-dismissal";

interface PlaygroundPromoStripProps {
  promo?: PublicPlaygroundPromo;
  locale: Locale;
}

type StripState = "expanded" | "collapsed" | "hidden";

const DHAKA = "Asia/Dhaka";
const DAY_MS = 86_400_000;

/**
 * The admin-managed course promo strip (`GR-PGP01`), rendered inside the
 * Playground's `notice` slot. Reading order is the founder's (2026-09-17):
 * the course name is the headline, the price comes next with the list price
 * struck through when a sale undercuts it, and everything else (the admin's
 * line, the plan name, the start date, seats, coupon, days left) is one
 * small muted line under those two. Rendered inside the
 * Playground's `notice` slot next to `PlaygroundSiteNoticeRow` and styled
 * for the same dark toolbar chrome. Three states: expanded (the strip),
 * collapsed (a small pill that reopens it) and hidden (the admin chose
 * "hide" for dismissals). The state a browser last chose is applied by an
 * inline script before paint, exactly as the site notice does, so the server
 * render carries the strip at full height and nothing shifts on hydration;
 * React then adopts that state in an effect and owns it from there.
 *
 * Never rendered for a visitor who already owns the course, and never on
 * the embed widget (that page fetches no platform settings at all).
 */
export function PlaygroundPromoStrip({ promo, locale }: PlaygroundPromoStripProps) {
  const t = useTranslations("playground.promo");
  const auth = useOptionalAuth();
  const token = auth?.getToken() ?? undefined;
  const { data: library } = useLibrary(token);
  const [state, setState] = useState<StripState>("expanded");
  const [daysLeft, setDaysLeft] = useState<number | null>(null);

  const id = promo?.id ?? "";
  const dismissMode = promo?.dismissMode ?? "collapse";
  const cohortStartAt = promo?.cohortStartAt ?? null;

  useEffect(() => {
    if (!id) return;
    try {
      if (window.localStorage.getItem(PLAYGROUND_PROMO_DISMISSAL_KEY) === id) {
        setState(dismissMode === "hide" ? "hidden" : "collapsed");
      }
    } catch {
      /* storage unavailable: stay expanded */
    }
  }, [id, dismissMode]);

  useEffect(() => {
    if (!cohortStartAt) return;
    const start = new Date(cohortStartAt).getTime();
    if (Number.isNaN(start)) return;
    setDaysLeft(Math.max(0, Math.ceil((start - Date.now()) / DAY_MS)));
  }, [cohortStartAt]);

  if (!promo) return null;
  if (library?.courses.some((c) => c.slug === promo.courseSlug)) return null;

  const courseTitle = getLocalizedField(promo.courseTitle_en, promo.courseTitle_bn, locale) ?? promo.courseTitle_en;
  const headline = getLocalizedField(promo.headline_en, promo.headline_bn, locale)?.trim() ?? "";
  const planName = getLocalizedField(promo.planName_en, promo.planName_bn, locale)?.trim() ?? "";
  const ctaLabel = getLocalizedField(promo.ctaLabel_en, promo.ctaLabel_bn, locale)?.trim() || t("cta");
  const startDate = promo.cohortStartAt ? formatDate(promo.cohortStartAt, locale, { timeZone: DHAKA }) : "";
  const money = (amount: number) =>
    promo.currency === "BDT" ? formatBdt(amount) : formatPrice(amount, promo.currency);
  const price = promo.fromPrice !== null ? money(promo.fromPrice) : "";
  const listPrice = promo.listPrice !== null && promo.fromPrice !== null && promo.listPrice > promo.fromPrice ? money(promo.listPrice) : "";
  const discountPct = listPrice && promo.listPrice && promo.fromPrice !== null ? Math.round(((promo.listPrice - promo.fromPrice) / promo.listPrice) * 100) : 0;
  const seatsLeft =
    promo.seatsTotal !== null && promo.seatsTaken !== null ? Math.max(0, promo.seatsTotal - promo.seatsTaken) : null;
  const coverUrl = promo.thumbnailImageId ? cfImageUrl(promo.thumbnailImageId, "card") : "";
  const initials = promo.courseTitle_en
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  const href =
    `/${locale}/courses/${promo.courseSlug}` +
    `?utm_source=playground&utm_medium=promo-strip&utm_campaign=${encodeURIComponent(promo.id)}` +
    (promo.couponCode ? `&coupon=${encodeURIComponent(promo.couponCode)}` : "");

  const wrapperId = `playground-promo-${promo.id}`;

  function handleDismiss() {
    persistPlaygroundPromoDismissed(promo!.id);
    setState(dismissMode === "hide" ? "hidden" : "collapsed");
  }

  function handleReopen() {
    clearPlaygroundPromoDismissed();
    setState("expanded");
  }

  const facts: string[] = [];
  if (headline) facts.push(headline);
  if (planName) facts.push(planName);
  if (startDate) facts.push(t("startsOn", { date: startDate }));
  if (seatsLeft !== null) facts.push(t("seatsLeft", { count: seatsLeft }));
  if (promo.couponCode) facts.push(t("coupon", { code: promo.couponCode }));

  return (
    <div
      id={wrapperId}
      data-state={state}
      className="group data-[state=hidden]:hidden"
    >
      <section
        aria-labelledby={`${wrapperId}-headline`}
        className="relative grid grid-cols-[auto_1fr] items-center gap-3 group-data-[state=collapsed]:hidden sm:grid-cols-[auto_1fr_auto]"
      >
        <span className="absolute -left-3 top-0 h-full w-[3px] bg-[#E94D1D] sm:-left-4" aria-hidden />
        <a
          href={href}
          tabIndex={-1}
          aria-hidden
          className="relative block h-12 w-[72px] shrink-0 overflow-hidden rounded-md bg-[radial-gradient(120%_120%_at_0%_0%,#F26A3A_0%,#E94D1D_45%,#7A1F0A_100%)]"
        >
          {coverUrl ? (
            <Image src={coverUrl} alt="" fill sizes="72px" className="object-cover" />
          ) : (
            <span className="grid h-full w-full place-items-center text-sm font-extrabold tracking-tight text-white">
              {initials}
            </span>
          )}
        </a>
        <div className="min-w-0 text-sm">
          <p id={`${wrapperId}-headline`} className="text-base font-bold leading-snug text-white">
            {courseTitle}
          </p>
          {price ? (
            <p className="leading-snug">
              <span className="font-bold text-white">{promo.planSlug ? price : t("from", { price })}</span>
              {listPrice ? (
                <>
                  {" "}
                  <span className="text-white/50 line-through">{listPrice}</span>
                  {" "}
                  <span className="text-xs font-bold text-[#FFAB8F]">{t("off", { percent: discountPct })}</span>
                </>
              ) : null}
            </p>
          ) : null}
          <p className="text-xs text-white/70">
            {facts.join(" · ")}
            {daysLeft !== null ? (
              <>
                {" "}
                <span className="inline-flex items-center rounded-md bg-white/10 px-1.5 py-0.5 text-xs font-bold tabular-nums text-white">
                  {t("daysLeft", { count: daysLeft })}
                </span>
              </>
            ) : null}
          </p>
        </div>
        <div className="col-span-2 flex items-center gap-2 sm:col-span-1">
          <a
            href={href}
            className="inline-flex min-h-[44px] items-center rounded-lg bg-[#E94D1D] px-4 text-sm font-bold text-white transition hover:bg-[#D14318] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C] sm:min-h-[36px]"
          >
            {ctaLabel}
          </a>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={t("dismissAriaLabel")}
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-white/15 text-white/75 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C] sm:min-h-[36px] sm:min-w-[36px]"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </section>

      <div className="flex justify-end group-data-[state=expanded]:hidden">
        <button
          type="button"
          onClick={handleReopen}
          aria-label={t("reopenAriaLabel")}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-[#E94D1D]/55 bg-[#17171A] py-1 pl-1 pr-3 text-xs font-semibold text-white transition hover:bg-[#E94D1D]/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C] sm:min-h-[32px]"
        >
          <span className="relative block h-6 w-6 shrink-0 overflow-hidden rounded-full bg-[radial-gradient(120%_120%_at_0%_0%,#F26A3A,#7A1F0A)]">
            {coverUrl ? (
              <Image src={coverUrl} alt="" fill sizes="24px" className="object-cover" />
            ) : (
              <span className="grid h-full w-full place-items-center text-[9px] font-extrabold text-white">{initials}</span>
            )}
          </span>
          <span className="max-w-[46vw] truncate sm:max-w-none">
            {courseTitle}
            {startDate ? ` · ${startDate}` : ""}
          </span>
          <ChevronRight className="h-3.5 w-3.5 text-[#FFAB8F]" aria-hidden />
        </button>
      </div>

      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `(function(){try{if(window.localStorage.getItem(${JSON.stringify(
            PLAYGROUND_PROMO_DISMISSAL_KEY,
          )})===${JSON.stringify(promo.id)}){var el=document.getElementById(${JSON.stringify(
            wrapperId,
          )});if(el)el.setAttribute("data-state",${JSON.stringify(
            dismissMode === "hide" ? "hidden" : "collapsed",
          )});}}catch(e){}})();`,
        }}
      />
    </div>
  );
}
